import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import { fetchLabWiseUtilization, fetchAllLabWiseUtilization } from "../../../API_Service/Dashboard_Slice";
import { companyID, Dropdown } from "../../../Common/UIComponents";

const LabWiseTestTypeAnalysis = ({ selectedFacilityID = 0, facilityList = [] }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testTypeData, setTestTypeData] = useState([]);
  const [chartOptions, setChartOptions] = useState(null);
  const [chartSeries, setChartSeries] = useState([]);
  const [selectedLab, setSelectedLab] = useState(selectedFacilityID);
  const [allLabs, setAllLabs] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalBillable: 0,
    totalNonBillable: 0,
    totalHours: 0,
  });

  const loadLabTestTypeData = useCallback(() => {
    if (!selectedLab) {
      setError("Please select a laboratory");
      setTestTypeData([]);
      return;
    }

    if (!companyID) {
      setError("Company ID not found");
      return;
    }

    console.log("📊 Loading Lab Test Type Analysis - FacilityID:", selectedLab, "CompanyID:", companyID);
    setIsLoading(true);
    setError(null);

    dispatch(fetchLabWiseUtilization({ companyID, facilityID: selectedLab }))
      .then((result) => {
        console.log("🔍 Lab Test Type Result:", result);

        let data = result.payload;

        if (result.payload && result.payload.data && Array.isArray(result.payload.data)) {
          data = result.payload.data;
        } else if (Array.isArray(result.payload)) {
          data = result.payload;
        }

        if (!Array.isArray(data) || data.length === 0) {
          setError("No test type data available for this lab");
          setTestTypeData([]);
          setChartSeries([]);
          return;
        }

        console.log("📋 Test Type Data:", data);

        const testTypeNames = data.map((t) => t.TestTypeName || "Unknown");
        const billableHours = data.map((t) => parseFloat(t.BillableHours) || 0);
        const nonBillableHours = data.map((t) => parseFloat(t.NonBillableHours) || 0);
        const totalHours = billableHours.map((b, i) => b + nonBillableHours[i]);

        const totalBillableHours = billableHours.reduce((a, b) => a + b, 0);
        const totalNonBillableHours = nonBillableHours.reduce((a, b) => a + b, 0);
        const totalAllHours = totalBillableHours + totalNonBillableHours;

        setSummaryData({
          totalBillable: totalBillableHours,
          totalNonBillable: totalNonBillableHours,
          totalHours: totalAllHours,
        });

        const tableData = testTypeNames.map((name, i) => ({
          testType: name,
          billableHours: billableHours[i].toFixed(2),
          nonBillableHours: nonBillableHours[i].toFixed(2),
          totalHours: totalHours[i].toFixed(2),
        }));

        setTestTypeData(tableData);
        setChartSeries(totalHours);
        setChartOptions({
          chart: {
            type: "donut",
            background: "transparent",
            animations: {
              enabled: true,
              speed: 800,
              animateGradually: {
                enabled: true,
                delay: 150,
              },
              dynamicAnimation: {
                enabled: true,
                speed: 150,
              },
            },
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: true,
                reset: true,
              },
            },
          },
          colors: ["#06B6D4", "#8B5CF6", "#FB923C", "#10B981", "#1E40AF", "#EC4899", "#3B82F6"],
          labels: testTypeNames,
          legend: {
            position: "bottom",
            fontSize: "13px",
            fontWeight: 600,
            labels: {
              colors: "#475569",
              useSeriesColors: false,
            },
          },
          plotOptions: {
            pie: {
              donut: {
                size: "65%",
                labels: {
                  show: true,
                  name: {
                    show: true,
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#475569",
                  },
                  value: {
                    show: true,
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#1e293b",
                  },
                  total: {
                    show: true,
                    label: "Total Hours",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#475569",
                  },
                },
              },
            },
          },
          dataLabels: {
            enabled: true,
            formatter: (v) => v.toFixed(1) + "%",
            style: {
              fontSize: "12px",
              fontWeight: 700,
              colors: ["#ffffff"],
            },
          },
          states: {
            hover: {
              filter: {
                type: "darken",
                value: 0.15,
              },
            },
            active: {
              filter: {
                type: "darken",
                value: 0.15,
              },
            },
          },
          tooltip: {
            enabled: true,
            theme: "light",
            style: {
              fontSize: "12px",
            },
            y: {
              formatter: (v) => v.toFixed(2) + " hrs",
              title: {
                formatter: (seriesName) => seriesName + ": ",
              },
            },
            marker: {
              show: true,
            },
            style: {
              padding: "10px 14px",
              borderRadius: "8px",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
            },
          },
        });
        setError(null);
      })
      .catch((err) => {
        console.error("❌ Lab Test Type Error:", err);
        setError(err?.message || "Failed to load test type analysis");
        setTestTypeData([]);
        setChartSeries([]);
      })
      .finally(() => setIsLoading(false));
  }, [selectedLab, companyID, dispatch]);

  useEffect(() => {
    if (selectedLab) loadLabTestTypeData();
  }, [selectedLab, loadLabTestTypeData]);

  useEffect(() => {
    if (selectedFacilityID > 0) setSelectedLab(selectedFacilityID);
  }, [selectedFacilityID]);

  // Fetch the complete lab list (not limited to top 10) for the dropdown
  useEffect(() => {
    if (!companyID) return;

    dispatch(fetchAllLabWiseUtilization({ companyID }))
      .then((result) => {
        let data = result.payload;

        if (result.payload && result.payload.data && Array.isArray(result.payload.data)) {
          data = result.payload.data;
        } else if (Array.isArray(result.payload)) {
          data = result.payload;
        } else {
          data = [];
        }

        const mapped = data
          .map((f) => ({
            id: f.FacilityID || f.facilityID || 0,
            name: f.FacilityName || f.facilityName || "Unknown",
          }))
          .filter((f) => f.id);

        setAllLabs(mapped);
      })
      .catch((err) => {
        console.error("Failed to load full lab list:", err);
      });
  }, [dispatch]);

  // Prefer the full lab list (unlimited) over the top-10 list handed down
  // from the Lab-wise Project Utilization chart, so every lab shows up here.
  const labSourceList = allLabs && allLabs.length > 0
    ? allLabs
    : facilityList.map((lab) => ({ id: lab.FacilityID, name: lab.FacilityName }));

  const labDropdownOptions = useMemo(
    () => [
      { label: "-- Select Lab --", value: 0 },
      ...labSourceList.map((lab) => ({ label: lab.name, value: lab.id })),
    ],
    [labSourceList],
  );

  return (
    <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-200 p-8 shadow-lg">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">Lab-wise Test Type Analysis</h3>
        <div className="w-64">
          <Dropdown
            options={labDropdownOptions}
            value={selectedLab}
            onChange={(val) => setSelectedLab(Number(val))}
            placeholder="-- Select Lab --"
          />
        </div>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6"><p className="text-red-600">{error}</p></div>}

      {selectedLab > 0 && testTypeData.length > 0 ? (
        <>
          {/* KPI Cards - Top */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            <div className="bg-blue-50 rounded-xl p-6"><p className="text-sm font-semibold text-blue-600 mb-2">Total Billable Hours</p><p className="text-3xl font-bold text-blue-700">{summaryData.totalBillable.toFixed(0)}</p></div>
            <div className="bg-purple-50 rounded-xl p-6"><p className="text-sm font-semibold text-purple-600 mb-2">Total Non-Billable Hours</p><p className="text-3xl font-bold text-purple-700">{summaryData.totalNonBillable.toFixed(0)}</p></div>
            <div className="bg-slate-100 rounded-xl p-6"><p className="text-sm font-semibold text-slate-600 mb-2">Total Hours</p><p className="text-3xl font-bold text-slate-700">{summaryData.totalHours.toFixed(0)}</p></div>
          </div>

          {/* Chart and Table */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="h-[370px] flex items-center justify-center">
              {chartOptions && <Chart options={chartOptions} series={chartSeries} type="donut" height={370} />}
            </div>
            <div>
              <table className="w-full text-sm">
                <thead><tr className="border-b-2 border-slate-200 bg-slate-50"><th className="px-4 py-3 text-left">Test Type</th><th className="px-4 py-3 text-right">Billable Hrs</th><th className="px-4 py-3 text-right">Non-Billable Hrs</th><th className="px-4 py-3 text-right">Total Hrs</th></tr></thead>
                <tbody>{testTypeData.map((row, idx) => (<tr key={idx} className="border-b border-slate-200"><td className="px-4 py-3">{row.testType}</td><td className="px-4 py-3 text-right">{row.billableHours}</td><td className="px-4 py-3 text-right">{row.nonBillableHours}</td><td className="px-4 py-3 text-right">{row.totalHours}</td></tr>))}</tbody>
              </table>
            </div>
          </div>
        </>
      ) : <div className="text-center text-slate-500 py-12"><p>Please select a laboratory</p></div>}
    </div>
  );
};

export default LabWiseTestTypeAnalysis;
